import axios from 'axios';
import Cookies from 'js-cookie';

const backendUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');

const axiosInstance = axios.create({
  baseURL: backendUrl,
});

// Helper for immediate logout and redirect
export const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('userToken');
  localStorage.removeItem('accessToken');
  sessionStorage.clear();
  try {
    Cookies.remove('refreshToken');
    Cookies.remove('token');
  } catch (e) {
    // Ignore cookie removal error
  }
  if (window.location.pathname !== '/signin') {
    window.location.href = '/signin';
  }
};

// Helper to check if a JWT token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const clean = token.replace(/^Bearer\s+/i, '').trim();
    const parts = clean.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

// Request interceptor to attach the token & verify expiration before sending
axiosInstance.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('token') ||
      sessionStorage.getItem('token') ||
      localStorage.getItem('userToken') ||
      sessionStorage.getItem('userToken') ||
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('accessToken');

    if (token) {
      if (isTokenExpired(token) && !config.url?.includes('admin/signin') && !config.url?.includes('admin/refresh')) {
        forceLogout();
        return Promise.reject(new Error('Session expired'));
      }

      if (!config.url?.includes('admin/signin')) {
        const cleanToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        if (config.headers && typeof config.headers.set === 'function') {
          config.headers.set('Authorization', cleanToken);
          config.headers.set('x-access-token', token.replace(/^Bearer\s+/i, ''));
        } else {
          config.headers = config.headers || {};
          config.headers['Authorization'] = cleanToken;
          config.headers.Authorization = cleanToken;
          config.headers['x-access-token'] = token.replace(/^Bearer\s+/i, '');
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and auto-logout on 401/403
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const status = error.response.status;

      if ((status === 401 || status === 403) && !originalRequest?.url?.includes('admin/signin')) {
        if (originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = Cookies.get('refreshToken');
            if (!refreshToken) {
              forceLogout();
              return Promise.reject(error);
            }

            const response = await axios.post(`${backendUrl}/admin/refresh`, { token: refreshToken }, {
              withCredentials: true,
            });

            const newToken = response.data?.access_token || response.data?.token;

            if (newToken) {
              localStorage.setItem('token', newToken);
              axiosInstance.defaults.headers['Authorization'] = `Bearer ${newToken}`;
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            } else {
              forceLogout();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            forceLogout();
            return Promise.reject(refreshError);
          }
        } else {
          forceLogout();
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
